import { useEffect, useState, useCallback, useRef } from "react";
import {
  Truck, MapPin, Clock, LogOut, Play, Square, Loader2, ChevronRight, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { fmtHours, fmtTime, fmtDate, weekMonday, weekRangeLabel } from "@/lib/format";

function useShiftTimer(punchIn) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!punchIn) return;
    const tick = () => setElapsed((Date.now() - new Date(punchIn).getTime()) / 1000);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [punchIn]);
  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = Math.floor(elapsed % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [week, setWeek] = useState(0);
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [truck, setTruck] = useState(user.truck_number || "");
  const [note, setNote] = useState("");
  const [loc, setLoc] = useState(null);
  const clock = useShiftTimer(status?.active ? status.shift.punch_in : null);

  const loadStatus = useCallback(async () => {
    const { data } = await api.get("/punch/status");
    setStatus(data);
    if (data.active && data.shift.truck_number) setTruck(data.shift.truck_number);
  }, []);

  const loadSheet = useCallback(async () => {
    const { data } = await api.get(`/timesheets/me?week=${weekMonday(week)}`);
    setSheet(data);
  }, [week]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadSheet(); }, [loadSheet]);

  // capture GPS
  const captureLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          const label = `${latitude.toFixed(4)}° N, ${Math.abs(longitude).toFixed(4)}° O`;
          resolve({ latitude, longitude, location_label: label });
        },
        () => resolve({ latitude: 45.5017, longitude: -73.5673, location_label: "Montréal, QC (approx.)" }),
        { timeout: 5000 }
      );
    });

  useEffect(() => {
    captureLocation().then(setLoc);
  }, []);

  const punch = async (dir) => {
    setBusy(true);
    try {
      const geo = (await captureLocation()) || {};
      const { data } = await api.post(`/punch/${dir}`, {
        truck_number: truck,
        note,
        ...geo,
      });
      toast.success(dir === "in" ? "Punch In enregistré avec succès" : "Punch Out enregistré avec succès");
      setNote("");
      await Promise.all([loadStatus(), loadSheet()]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const active = status?.active;

  return (
    <div className="min-h-screen pb-16 relative z-10">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0B0F17]/80 border-b border-[#1E293B]">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#F59E0B] flex items-center justify-center">
              <Truck className="w-5 h-5 text-[#0B0F17]" />
            </div>
            <div>
              <p className="font-display font-bold leading-none">{user.name}</p>
              <p className="text-xs text-slate-400 mt-0.5">Chauffeur</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="logout-button"
            onClick={logout}
            className="text-slate-400 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-1" /> Sortir
          </Button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-6 space-y-6">
        {/* Punch terminal */}
        <section className="ct-card p-6 fade-in" data-testid="punch-terminal">
          <div className="flex items-center justify-between mb-5">
            <span
              className={`text-xs font-semibold px-3 py-1 rounded-full ${
                active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"
              }`}
              data-testid="punch-status-badge"
            >
              {active ? "● En poste" : "○ Hors service"}
            </span>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {loc ? loc.location_label : "Localisation…"}
            </span>
          </div>

          <div className="flex flex-col items-center">
            <button
              onClick={() => punch(active ? "out" : "in")}
              disabled={busy}
              data-testid={active ? "punch-out-button" : "punch-in-button"}
              className={`relative w-40 h-40 rounded-full flex flex-col items-center justify-center font-display font-extrabold text-lg transition-transform active:scale-95 disabled:opacity-60 ${
                active
                  ? "bg-[#EF4444] text-white pulse-ring"
                  : "bg-[#10B981] text-[#0B0F17] pulse-ring"
              }`}
            >
              {busy ? (
                <Loader2 className="w-9 h-9 animate-spin" />
              ) : active ? (
                <>
                  <Square className="w-8 h-8 mb-1" fill="currentColor" />
                  PUNCH OUT
                </>
              ) : (
                <>
                  <Play className="w-9 h-9 mb-1" fill="currentColor" />
                  PUNCH IN
                </>
              )}
            </button>

            {active && (
              <div className="mt-5 text-center" data-testid="shift-timer">
                <p className="text-xs text-slate-400 uppercase tracking-wider">Durée du poste</p>
                <p className="font-mono text-3xl font-bold text-emerald-400 mt-1">{clock}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Début : {fmtTime(status.shift.punch_in)}
                </p>
              </div>
            )}
          </div>

          {/* Inputs */}
          <div className="mt-6 grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs text-slate-400">Numéro de camion</label>
              <Input
                value={truck}
                disabled={active}
                onChange={(e) => setTruck(e.target.value)}
                placeholder="Ex. 204"
                data-testid="truck-number-input"
                className="mt-1 bg-[#0B0F17] border-[#1E293B]"
              />
            </div>
            {!active && (
              <div>
                <label className="text-xs text-slate-400">Note (optionnel)</label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Trajet / remarque"
                  data-testid="note-input"
                  className="mt-1 bg-[#0B0F17] border-[#1E293B]"
                />
              </div>
            )}
          </div>
        </section>

        {/* Weekly cumulative */}
        {sheet && (
          <section className="ct-card p-5 fade-in" data-testid="weekly-tracker">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Button
                  size="icon" variant="ghost"
                  className="h-7 w-7 text-slate-400"
                  data-testid="week-prev-button"
                  onClick={() => setWeek((w) => w - 1)}
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                </Button>
                <span className="text-sm font-medium flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-[#F59E0B]" />
                  {weekRangeLabel(sheet.week_start)}
                </span>
                <Button
                  size="icon" variant="ghost"
                  className="h-7 w-7 text-slate-400 disabled:opacity-30"
                  disabled={week >= 0}
                  data-testid="week-next-button"
                  onClick={() => setWeek((w) => Math.min(0, w + 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-end justify-between mb-2">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Heures cumulées</p>
                <p className="font-mono text-3xl font-bold" data-testid="weekly-total">
                  {fmtHours(sheet.total)}
                </p>
              </div>
              <div className="text-right text-xs">
                <p className="text-slate-400">Régulier {fmtHours(sheet.regular)}</p>
                <p className="text-[#F59E0B]">Suppl. {fmtHours(sheet.overtime)}</p>
              </div>
            </div>
            <Progress value={Math.min((sheet.total / 40) * 100, 100)} className="h-2 bg-[#0B0F17]" />
            <p className="text-xs text-slate-500 mt-1.5">Objectif régulier : 40h / semaine</p>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-1.5 mt-4">
              {sheet.days.map((d) => {
                const dow = ["Dim","Lun","Mar","Mer","Jeu","Ven","Sam"][new Date(d.date+"T00:00:00").getDay()];
                return (
                  <div key={d.date} className="text-center">
                    <p className="text-[10px] text-slate-500 mb-1">{dow}</p>
                    <div
                      className={`rounded-lg py-2 text-xs font-mono font-bold ${
                        d.hours > 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-[#0B0F17] text-slate-600"
                      }`}
                    >
                      {d.hours > 0 ? d.hours.toFixed(1) : "—"}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Daily history */}
        {sheet && (
          <section data-testid="daily-history">
            <h3 className="font-display font-bold text-lg mb-3 px-1">Détail par jour</h3>
            <div className="space-y-2.5">
              {sheet.days.filter((d) => d.shifts.length > 0).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">Aucune entrée cette semaine.</p>
              )}
              {sheet.days
                .filter((d) => d.shifts.length > 0)
                .map((d) => (
                  <div key={d.date} className="ct-card p-4" data-testid={`day-card-${d.date}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold capitalize">{fmtDate(d.date)}</p>
                      <span className="font-mono font-bold text-[#F59E0B]">{fmtHours(d.hours)}</span>
                    </div>
                    {d.shifts.map((s) => (
                      <div key={s.id} className="flex items-center gap-3 text-sm text-slate-300 py-1 border-t border-[#1E293B] first:border-t-0">
                        <Clock className="w-3.5 h-3.5 text-slate-500" />
                        <span className="font-mono">{fmtTime(s.punch_in)}</span>
                        <ChevronRight className="w-3 h-3 text-slate-600" />
                        <span className="font-mono">{s.punch_out ? fmtTime(s.punch_out) : "En cours"}</span>
                        {s.truck_number && <span className="ml-auto text-xs text-slate-500">🚚 #{s.truck_number}</span>}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
